import React, { useRef, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { GT_8_CLASS, LT_8_CLASS } from 'site-modules/shared/constants/top-rated/top-rated';
import { topRatedHelpers } from 'site-modules/shared/utils/top-rated/top-rated-helpers';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { useOnScreen } from 'site-modules/shared/hooks/use-on-screen';
import { CmsEntities } from 'client/data/models/cms';
import { LaurelIcons } from 'site-modules/editorial/components/top-rated/icons/laurel-icons';
import { ElectricBolt } from 'site-modules/editorial/components/top-rated/icons/electric-bolt';

import 'client/site-modules/editorial/components/top-rated/category-top-nav/nav-anchor-animation.scss';

const ELECTRIC_TEXT = {
  true: 'EV',
  false: 'Electric',
};

function getIsBotb(categoryTitle) {
  return categoryTitle.toLowerCase() === 'best of the best';
}

function renderElectricCategoryTitle(categoryTitle, isNarrow) {
  const isBotb = getIsBotb(categoryTitle);
  const categoryText = categoryTitle.replace(ELECTRIC_TEXT.false, '');
  return (
    <Fragment>
      {!isNarrow && !isBotb && (
        <span>
          Top Rated
          <ElectricBolt size={14} wrapperClass="pos-r d-inline-block mx-0_25" />
        </span>
      )}
      <span
        className={classnames('electric-text', {
          'fst-italic': !isNarrow,
        })}
      >
        {ELECTRIC_TEXT[isNarrow]}
      </span>
      {categoryText}
    </Fragment>
  );
}

function renderIceCategoryTitle(categoryTitle, isMobile) {
  const isBotb = getIsBotb(categoryTitle);
  return (
    <span>
      {!isMobile && !isBotb && 'Top Rated'} {categoryTitle}
    </span>
  );
}

export function NavAnchor({
  entry,
  index = 0,
  tileNumClass = null,
  isOddNumLength = false,
  isMobile = false,
  year = null,
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef();
  const [isOnScreen] = useOnScreen(ref, {
    root: null,
    rootMargin: '0px',
    threshold: 0.1,
  });
  const isSmallIcon = !isOddNumLength || index > 0;
  const isLargeIcon = index === 0 && isOddNumLength;
  const categoryTitle = entry.metadata('categoryTitle').value();
  const categoryTitleToUse = isMobile ? categoryTitle.replace(ELECTRIC_TEXT.true, ELECTRIC_TEXT.false) : categoryTitle;
  const isTruckCategory = categoryTitle.toLowerCase() === 'truck' && index > 0;
  const vehicleTitleToUse = isLargeIcon
    ? entry.metadata('vehicleTitle').value().replace(year, '').trim()
    : entry.metadata('vehicleTitle').value();
  const isElectricCategory = categoryTitle.toLowerCase().includes('electric');
  const renderCategoryTitle = isNarrowOnly =>
    isElectricCategory
      ? renderElectricCategoryTitle(categoryTitleToUse, isNarrowOnly || isMobile)
      : renderIceCategoryTitle(categoryTitleToUse, isNarrowOnly || isMobile);

  if (isOnScreen && !isIntersecting) {
    setIsIntersecting(true);
  }

  return (
    <a
      href={entry.metadata('tileURL').value()}
      key={`entry-${categoryTitle}-${index + 1}`}
      className={classnames(
        'pos-r nav-anchor with-animation d-flex flex-column align-items-center py-1 px-0_5 px-md-1',
        {
          'd-lg-flex ms-lg-0': tileNumClass === LT_8_CLASS,
          'mx-0_5 slick-slide': tileNumClass === GT_8_CLASS,
          'is-intersecting': isIntersecting,
          'justify-content-center pt-1': isLargeIcon,
          'pt-1_25 pt-lg-0_5 pb-1': isSmallIcon,
        }
      )}
      ref={ref}
      style={isLargeIcon ? { minWidth: '280px' } : null}
    >
      {isLargeIcon && <LaurelIcons maxWidth="324px" />}
      <figure
        className={classnames('pos-r category-icon mx-auto mb-0 d-flex justify-content-center align-items-center', {
          'mx-lg-0_5': tileNumClass === LT_8_CLASS,
          'mt-0_5 mt-lg-2': isLargeIcon,
          'mt-lg-0_5': isSmallIcon,
        })}
        style={topRatedHelpers.get2026IconSize(index, isOddNumLength)}
      >
        <img
          src={getStaticImageUrl(entry.metadata('categoryIcon').value())}
          alt={`${entry.metadata('categoryTitle').value()} logo`}
          width={topRatedHelpers.get2026IconSize(index, isOddNumLength, isTruckCategory).width}
          height={topRatedHelpers.get2026IconSize(index, isOddNumLength, isTruckCategory).height}
        />
      </figure>
      <div
        className={classnames('title-info pos-r', {
          'mb-2 mt-0 justify-content-center': isLargeIcon,
          'small-icon d-flex flex-column justify-content-start align-items-center': isSmallIcon,
        })}
        style={{ flex: isSmallIcon ? '1 1 0' : null }}
      >
        {isLargeIcon && <div className="size-20 text-white">{year}</div>}
        <span
          className={classnames('category-title d-flex fw-bold', {
            'd-lg-block': tileNumClass === LT_8_CLASS,
            'justify-content-center align-items-center text-yellow-30 text-uppercase size-20 py-0_25': isLargeIcon,
            'size-14 justify-content-center justify-content-lg-start text-white': isSmallIcon,
          })}
        >
          <span className="hidden-lg-down">{renderCategoryTitle(false)}</span>
          <span className="hidden-lg-up">{renderCategoryTitle(true)}</span>
        </span>
        <span
          className={classnames('vehicle-title mt-0_25 text-center text-blue-90', {
            'size-16': isLargeIcon,
            'size-12': isSmallIcon,
          })}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: '2',
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {vehicleTitleToUse}
        </span>
      </div>
    </a>
  );
}

NavAnchor.propTypes = {
  entry: CmsEntities.Content.isRequired,
  index: PropTypes.number,
  tileNumClass: PropTypes.string,
  isOddNumLength: PropTypes.bool,
  isMobile: PropTypes.bool,
  year: PropTypes.string,
};
